/**
 * Admin — Link this Portal user to a Books tenant.
 * Enables MIS widget on AdminHome + CRM auto-invoice on Won tickets.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Link2, Unlink, CheckCircle2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('emerald_token')}` });

const BooksLinkPage = () => {
  const [link, setLink] = useState(null);
  const [linked, setLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/portal-bridge/books/link`, { headers: authHeaders() });
      const body = await r.json();
      setLinked(body.linked); setLink(body.link);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/portal-bridge/books/link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (!r.ok) { toast({ title: (await r.json()).detail || 'Link failed', variant: 'destructive' }); return; }
      toast({ title: 'Linked to Books ✓' });
      setForm({ email: '', password: '' });
      load();
    } finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!window.confirm('Unlink this Portal account from Books?')) return;
    await fetch(`${API}/portal-bridge/books/link`, { method: 'DELETE', headers: authHeaders() });
    toast({ title: 'Unlinked' });
    load();
  };

  return (
    <div data-testid="books-link-page" className="max-w-2xl">
      <div className="mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Integrations</span>
        <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Emerald Books</h1>
        <p className="mt-2 text-sm text-[#0b1a2e]/60">
          Link your Portal account to a Books tenant to unlock the MIS widget on this dashboard
          and to auto-draft invoices when a CRM ticket is marked <em>Won</em>.
        </p>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : linked && link ? (
        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-1" />
            <div className="flex-1">
              <div className="text-lg font-medium text-[#0b1a2e]">Linked to <strong>{link.books_tenant_name}</strong></div>
              <div className="text-sm text-[#0b1a2e]/70 mt-1">{link.books_country_code} · {link.books_base_currency} · role: {link.books_role}</div>
              <div className="text-xs text-[#0b1a2e]/50 mt-1">Books email: {link.books_email}</div>
              <div className="text-xs text-[#0b1a2e]/40 mt-3">Linked {new Date(link.linked_at).toLocaleString()}</div>
            </div>
            <button onClick={unlink} data-testid="books-unlink"
              className="inline-flex items-center gap-1 px-4 py-2 border border-red-500 text-red-700 rounded-full text-sm font-semibold">
              <Unlink className="w-3.5 h-3.5" /> Unlink
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <div className="space-y-3">
            <L label="Books email">
              <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="books-link-email" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </L>
            <L label="Books password">
              <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="books-link-password" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </L>
            <div className="text-xs text-[#0b1a2e]/50">
              Don't have a Books tenant yet? <a href="/books/signup" className="text-[#0f5c3d] font-semibold">Sign up here</a> first, then come back and link.
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button onClick={submit} disabled={busy || !form.email || !form.password} data-testid="books-link-submit"
              className="inline-flex items-center gap-1 px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Link Books tenant
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const L = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default BooksLinkPage;
